import React, { forwardRef, useMemo } from "react";

import { faEllipsisH } from "@fortawesome/pro-solid-svg-icons/faEllipsisH";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";

import Email from "@mapmycustomers/shared/types/email/Email";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import isExternalEmail from "../util/isExternalEmail";
import removeThreadFromEmailHtml from "../util/removeThreadFromEmailHtml";
import sanitizeEmailHtml from "../util/sanitizeEmailHtml";

import styles from "./EmailHtml.module.scss";

interface EmailHtmlProps {
  className?: string;
  email: Email;
  showPreviousEmails: boolean;
}

const EmailHtml = forwardRef<HTMLDivElement, EmailHtmlProps>(
  ({ className, email, showPreviousEmails }, ref) => {
    const externalEmail = isExternalEmail(email);

    const [emailThreadHtmlVisible, , , toggleEmailThreadHtmlVisibility] = useBoolean(false, true);

    const [bodyHtml, threadHtml] = useMemo(() => {
      return removeThreadFromEmailHtml(email.body);
    }, [email]);

    const displayBodyHtml = useMemo(
      () => (externalEmail ? sanitizeEmailHtml(bodyHtml.innerHTML) : bodyHtml.innerHTML),
      [externalEmail, bodyHtml]
    );

    const displayThreadHtml = useMemo(() => {
      if (!threadHtml || !showPreviousEmails) {
        return null;
      }

      return externalEmail ? threadHtml : sanitizeEmailHtml(threadHtml);
    }, [externalEmail, threadHtml, showPreviousEmails]);

    return (
      <div className={cn(styles.container, className)}>
        <div dangerouslySetInnerHTML={{ __html: displayBodyHtml }} ref={ref} />
        {!!displayThreadHtml && (
          <>
            <Button
              className={styles.emailThreadHtmlToggleButton}
              onClick={toggleEmailThreadHtmlVisibility}
              size="small"
            >
              <FontAwesomeIcon icon={faEllipsisH} />
            </Button>
            {emailThreadHtmlVisible && (
              <div dangerouslySetInnerHTML={{ __html: displayThreadHtml }} ref={ref} />
            )}
          </>
        )}
      </div>
    );
  }
);

export default EmailHtml;
